import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useRoleActionContext } from '@/context/role-actions';
import { useDebounce } from '@/hooks/debounce';

import type { HelpStateFilter } from './help-centre.types';
import {
	fetchAdminArticle,
	fetchAdminArticles,
	fetchHelpArticle,
	fetchHelpCapabilities,
	fetchHelpSearch,
	fetchHelpTree,
	fetchModuleContext,
	fetchVersions,
	HelpCentreUnavailableError,
	normaliseHelpQuery,
	retryHelp,
} from './help-centre-api';
import { helpKeys, permissionScopeKey } from './help-centre-keys';

const useScope = () => permissionScopeKey(useRoleActionContext().snapshot);

/**
 * The capability handshake every Help Centre entry point hangs off.
 *
 * `available` is true only on a well-formed `enabled: true`. While loading, and on ANY failure
 * (404 from an older api.core, a 500, an HTML fallback page) it is false — so the "?" button, the
 * Help link and the palette's Guides group stay hidden rather than leading somewhere broken.
 */
export const useHelpCapabilities = () => {
	const scope = useScope();
	const query = useQuery({
		queryKey: helpKeys.capabilities(scope),
		queryFn: ({ signal }) => fetchHelpCapabilities(signal),
		staleTime: 10 * 60_000,
		retry: retryHelp,
	});
	return {
		available: query.data?.enabled === true,
		isLoading: query.isLoading,
		/** Definitively no Help Centre here: an older api.core, or the feature switched off. */
		absent: query.error instanceof HelpCentreUnavailableError || query.data?.enabled === false,
		/** A failure that is NOT "absent" (500, network) — worth a retry, not a "not installed" message. */
		isError: query.isError && !(query.error instanceof HelpCentreUnavailableError),
		refetch: query.refetch,
	};
};

export const useHelpTree = (enabled = true) => {
	const scope = useScope();
	return useQuery({
		queryKey: helpKeys.tree(scope),
		queryFn: ({ signal }) => fetchHelpTree(signal),
		enabled,
		staleTime: 5 * 60_000,
		retry: retryHelp,
	});
};

export const useHelpArticle = (slug: string | undefined) => {
	const scope = useScope();
	return useQuery({
		queryKey: helpKeys.article(scope, slug ?? ''),
		queryFn: ({ signal }) => fetchHelpArticle(slug!, signal),
		enabled: !!slug,
		staleTime: 5 * 60_000,
		retry: retryHelp,
	});
};

export const HELP_SEARCH_DEBOUNCE_MS = 200;

/**
 * Debounced 200ms. Under two characters nothing is sent — the server answers 400 `QUERY_LENGTH`,
 * which is not a failure the reader should see. `pageSize` is clamped to the server's 1–20.
 */
export const useHelpSearch = (q: string, { page = 1, pageSize = 20, enabled = true } = {}) => {
	const scope = useScope();
	const debounced = useDebounce(normaliseHelpQuery(q), HELP_SEARCH_DEBOUNCE_MS);
	const query = useQuery({
		queryKey: helpKeys.search(scope, debounced, page, pageSize),
		queryFn: ({ signal }) => fetchHelpSearch(debounced, page, Math.min(20, Math.max(1, pageSize)), signal),
		enabled: enabled && debounced.length >= 2,
		staleTime: 60_000,
		placeholderData: keepPreviousData,
		retry: retryHelp,
	});
	return { ...query, debouncedQuery: debounced, isSettling: debounced !== normaliseHelpQuery(q) };
};

export const useModuleContext = (key: string | null) => {
	const scope = useScope();
	return useQuery({
		queryKey: helpKeys.context(scope, key ?? ''),
		queryFn: ({ signal }) => fetchModuleContext(key!, signal),
		enabled: !!key,
		staleTime: 5 * 60_000,
		retry: retryHelp,
	});
};

// ── Editor ────────────────────────────────────────────────────

/** `enabled: false` until the caller is known to hold `help_centre_manage` — otherwise a guaranteed 403. */
export const useHelpAdminArticles = (
	filter: { moduleKey?: string; state?: HelpStateFilter; page: number; pageSize: number },
	enabled = true
) =>
	useQuery({
		queryKey: helpKeys.admin.list(filter),
		queryFn: ({ signal }) => fetchAdminArticles(filter, signal),
		enabled,
		placeholderData: keepPreviousData,
		retry: retryHelp,
	});

export const useHelpAdminArticle = (id: number | null) =>
	useQuery({
		queryKey: helpKeys.admin.article(id ?? 0),
		queryFn: ({ signal }) => fetchAdminArticle(id!, signal),
		enabled: id !== null && id > 0,
		// The editor owns its form state after load; a background refetch must not clobber typing.
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		retry: retryHelp,
	});

export const useHelpVersions = (id: number | null) =>
	useQuery({
		queryKey: helpKeys.admin.versions(id ?? 0),
		queryFn: ({ signal }) => fetchVersions(id!, signal),
		enabled: id !== null && id > 0,
		retry: retryHelp,
	});
